"use client"

import { Field, useFormik, FormikProvider } from "formik"
import { TextField } from "formik-mui"
import { useReCaptcha } from "next-recaptcha-v3"
import { useSession, signIn } from "next-auth/react"
import styles from "./Signup.module.scss"
import Row from "../Row/Row"
import Col from "../Col/Col"
import Spacer from "../Spacer/Spacer"
import { useEffect, useState, useRef } from "react"
import RegularButton from "../RegularButton/RegularButton"
import * as Yup from "yup"
import axios from "axios"
import { mergeUrl } from "@/utils/merge"
import { setUtmsToFormData } from "@/utils/utms"
import LinkedinIcon from "@/components/Icons/linkedin"
import { useTranslations, useLocale } from "next-intl"
import { getDefaultISO2Code } from "react-phone-hooks"
import { MuiTelInput } from "mui-tel-input"
// import Link from "next/link"

export default function Signup({
  className,
  texts,
  bottomText,
  form_id,
  eventSlug = null,
  withRadio = false,
  isOnline = false,
}) {
  const t = useTranslations()
  const session = useSession()
  const lang = useLocale()
  const phoneRef = useRef()

  let initialValues =
    session?.status === "authenticated"
      ? {
          ...session.data.user,
          exclude_from_maillists: true,
          is_already_client: false,
        }
      : {
          first_name: "",
          email: "",
          phone: "",
          exclude_from_maillists: true,
          is_already_client: false,
        }

  const { executeRecaptcha } = useReCaptcha()
  const [errorMessage, setErrorMessage] = useState("")

  const [isSuccess, setIsSuccess] = useState(false)

  const SignupSchema = Yup.object().shape({
    first_name: Yup.string().required(texts?.nameReq || t("form.nameReq")),
    email: Yup.string()
      .email(texts?.emailValid || t("form.emailValid"))
      .required(texts?.emailReq || t("form.emailReq")),
    phone: Yup.string()
      // .matches(/^\+[1-9]\d{1,14}$/, texts?.phoneValid || t("form.phoneValid"))
      .required(texts?.phoneReq || t("form.phoneReq")),
  })

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema: SignupSchema,
    enableReinitialize: true,
  })

  useEffect(() => {
    if (session?.status === "authenticated") {
      initialValues = { ...formik.values, ...session.data.user }
      formik.setValues(initialValues)
      formik.setFieldTouched("phone", true)
    }
  }, [session])

  const backToForm = () => {
    setIsSuccess(false)
    setErrorMessage("")
    formik.resetForm()
  }

  const getBaseUrl = (url) => {
    const urlObj = new URL(url)

    const baseUrl = `${urlObj.protocol}//${urlObj.host}/`

    return baseUrl
  }

  async function handleSubmit(values) {
    const token = await executeRecaptcha("lead")
    setErrorMessage("")

    // const formData = new FormData()
    const formData = {}
    setUtmsToFormData(formData)

    for (const [key, value] of Object.entries(values)) {
      if (key === "email") {
        formData[key] = value.toLowerCase()
      } else if (key === "exclude_from_maillists") {
        formData[key] = !value
      } else {
        formData[key] = value
      }
    }

    const currentUrl = new URL(window.location.href)

    if (lang) {
      formData["lang"] = lang
    }
    if (formData["company_name"] === "") {
      delete formData["company_name"]
    }
    if (session?.status === "authenticated") {
      formData["additional_data"] = {
        linkedin: session.data.token,
      }
    }

    formData["event_url"] = eventSlug
      ? `${getBaseUrl(currentUrl)}events/${eventSlug}` // TODO it was needed when used for render form on main, need to remove?
      : currentUrl.toString()

    formData["form_id"] = form_id
    formData["recaptcha"] = token

    await axios
      .post(
        mergeUrl(),
        // formData
        JSON.stringify(formData),
        {
          headers: {
            "Content-Type": "application/json",
            // "Referrer-Policy": "no-referrer-when-downgrade",
          },
        }
      )
      .then(({ data }) => {
        // this.loading = false
        setIsSuccess(true)

        if (_paq) {
          _paq.push([
            "trackEvent",
            "event",
            "click_register_success",
            "event_click_register_success",
          ])
        }

        formik.resetForm()
      })
      .catch((error) => {
        if (error.response.data.email) {
          setErrorMessage(
            error.response.data.email[0] ||
              texts?.someWrong ||
              t("form.someWrong")
          )
        } else {
          setErrorMessage(error.response.data.detail)
        }

        if (_paq) {
          _paq.push([
            "trackEvent",
            "event",
            "click_register_failed",
            "event_click_register_failed",
          ])
        }
      })
  }

  const fillWithLinkedin = () => {
    signIn("linkedin")

    if (_paq) {
      _paq.push([
        "trackEvent",
        "event",
        "form_autofill_Linkedin",
        "event_form_autofill_Linkedin",
      ])
    }
  }

  const onPhoneChange = (value, e) => {
    formik.setFieldValue("phone", e.numberValue)
  }

  return (
    <div className={`${styles.signup} ${className ? className : ""}`}>
      {!isSuccess && (
        <div className={styles.signup__header}>
          <h2
            dangerouslySetInnerHTML={{
              __html: texts?.title || t.raw("form.title"),
            }}
          ></h2>
        </div>
      )}
      {!isSuccess && !isOnline && (
        <div
          className={styles.signup__note}
          dangerouslySetInnerHTML={{
            __html: texts?.note || t.raw("form.note"),
          }}
        ></div>
      )}

      {errorMessage && (
        <div id="signup_error" className={`${styles.meet__error} form_error`}>
          {errorMessage}
        </div>
      )}
      {isSuccess ? (
        <div
          id="signup_success"
          className={`${styles.meet__success} registration_thank_you_message`}
        >
          <h3 className={styles.meetSuccess__title}>
            {texts?.thanks || t("form.thanks")}
          </h3>
          <p
            className={styles.meetSuccess__text}
            dangerouslySetInnerHTML={{ __html: texts?.thankyouText }}
          ></p>
          <button className={styles.meetSuccess__button} onClick={backToForm}>
            {texts?.back || t("form.back")}
          </button>
        </div>
      ) : (
        <FormikProvider value={formik}>
          <form
            id="form"
            className={styles.meet__form}
            onSubmit={formik.handleSubmit}
          >
            <Row className={styles.signup__perfields}>
              <Col cols="12" md="10" lg="12">
                <label
                  className={styles.signup__checkbox}
                  htmlFor="is_already_client"
                >
                  <Field
                    id="is_already_client"
                    className={styles.signup__checkbox_field}
                    type="checkbox"
                    name="is_already_client"
                    value={formik.values.is_already_client}
                    checked={formik.values.is_already_client}
                  />
                  <span className={styles.signup__checkbox_slider}></span>
                  <span className={styles.signup__checkbox_text}>
                    {texts?.isAlreadyClient || t("form.isAlreadyClient")}
                  </span>
                </label>
              </Col>
              <Spacer></Spacer>
              <Col cols="12" md="10" lg="12">
                {session?.status !== "authenticated" && !isSuccess && (
                  <div>
                    <RegularButton
                      className={`${styles.signup__with_btn} autofill_with_linkedin_btn`}
                      btnStyle="secondaryWhite"
                      text={t("form.linkedin")}
                      onClick={fillWithLinkedin}
                      pre_text={<LinkedinIcon />}
                    />
                  </div>
                )}
              </Col>
            </Row>
            <Row>
              <Col cols="12" md="10" lg="12" className={styles.meetForm__input}>
                <Field
                  component={TextField}
                  name="first_name"
                  type="text"
                  label={texts?.name || t("form.name")}
                  variant="standard"
                  value={formik.values.first_name || ""}
                  fullWidth
                />
              </Col>
              <Spacer></Spacer>
              <Col cols="12" md="10" lg="12">
                <Field
                  component={TextField}
                  name="last_name"
                  type="text"
                  label={texts?.surname || t("form.surname")}
                  variant="standard"
                  value={formik.values.last_name || ""}
                  fullWidth
                />
              </Col>
              <Col cols="12" md="10" lg="12">
                <Field
                  component={TextField}
                  name="email"
                  type="email"
                  label={texts?.email || t("form.email")}
                  variant="standard"
                  value={formik.values.email || ""}
                  fullWidth
                />
              </Col>
              <Spacer></Spacer>

              <Col
                cols="12"
                md="10"
                lg="12"
                className={styles.signup__phone_input_wrap}
              >
                <MuiTelInput
                  label={texts?.phone || t("form.phone")}
                  focusOnSelectCountry={true}
                  required
                  name="phone"
                  autoComplete="tel"
                  variant="standard"
                  fullWidth
                  value={formik.values.phone || ""}
                  onChange={onPhoneChange}
                  excludedCountries={["RU"]}
                  defaultCountry={getDefaultISO2Code().toUpperCase()}
                />
              </Col>
            </Row>
            <Row>
              <Col cols="12">
                <Field
                  component={TextField}
                  name="company_name"
                  type="text"
                  label={texts?.company || t("form.company")}
                  variant="standard"
                  value={formik.values.company_name || ""}
                  fullWidth
                />
              </Col>
            </Row>

            {withRadio && (
              <label className={styles.meetForm__radio}>
                <Field type="checkbox" name="exclude_from_maillists" />
                <span></span>
                <p
                  dangerouslySetInnerHTML={{
                    __html: texts?.marketingCom || t.raw("form.marketingCom"),
                  }}
                ></p>
              </label>
            )}

            <footer className={styles.meetForm__footer}>
              <RegularButton
                className={styles.meetForm__btn}
                type="submit"
                text={texts?.signup || t("form.signup")}
                btnStyle="white_no_corner"
              />
              {bottomText && (
                <div
                  className={styles.meetForm__textafter}
                  dangerouslySetInnerHTML={{ __html: bottomText }}
                ></div>
              )}
              {/* <p>
                {isOnline ? (
                  <>
                    Want to attend in person? Register{" "}
                    <a href="/cyprus2023vip/" target="_blank">
                      here
                    </a>{" "}
                    to visit us in Cyprus!
                  </>
                ) : (
                  <>
                    Can’t attend in person? Register{" "}
                    <a href="/cyprus2023/" target="_blank">
                      here
                    </a>{" "}
                    to watch the webinar!
                  </>
                )}
              </p> */}
            </footer>
          </form>
        </FormikProvider>
      )}
    </div>
  )
}
